import { JSONSchema7 } from 'json-schema';
import { types as commonTypes, customSchemas } from '@sitebuilder/common';
import RiskIndicator from './RiskIndicator';

const schema: JSONSchema7 = {
    title: 'Risk Indicator',
    type: 'object',
    required: [],
    properties: {
        styles: {
            type: 'string',
            description: 'A stylesheet',
            default: '',
        },
        ...customSchemas.textSchema.TextSchema.schema,
        ...customSchemas.styleSchema.StyleSchema.schema,
        ...customSchemas.layoutSchema.LayoutSchema.schema,
        riskIndicatorType: {
            type: 'string',
            title: 'Select risk indicator type',
            enum: ['Tile', 'Gauge chart', 'Images'],
            default: 'Tile',
        },
        riskIndicatorTitle: {
            type: 'string',
            title: 'Risk Indicator Title',
            default: '',
        },
        riskRatingsDataKey: {
            type: 'string',
            title: 'Risk Ratings Data Key',
            default: '',
        },
        riskRatings: {
            type: 'string',
            title: 'Risk Ratings',
            default: 'Low,,,,High',
            description: 'Enter comma seperated values',
        },
        displayRatingLabel: {
            type: 'boolean',
            description: 'Display rating label',
            default: false,
            title: 'Display rating label',
        },
        addTabIndexToSelectedTile: {
            type: 'boolean',
            default: false,
            title: 'Add Tab Index To Selected Tile',
        },
        ariaLabelsRiskRatings: {
            type: 'string',
            title: 'Risk Ratings Aria Labels',
            default: 'Low,Low to Medium,Medium,medium to High,High',
            description: 'Enter comma seperated values',
        },
        tileAriaLabel: {
            type: 'string',
            title: 'Risk Indicator Aria Label',
            default: 'Risk Rating',
        },
    },
    dependencies: {
        ...customSchemas.layoutSchema.LayoutSchema.dependencies,
        riskIndicatorType: {
            oneOf: [
                {
                    properties: {
                        riskIndicatorType: { enum: ['Tile'] },
                        isTileShapeSvg: {
                            type: 'boolean',
                            title: 'Render Svgs for tile shapes',
                            default: false,
                        },
                    },
                    dependencies: {
                        isTileShapeSvg: {
                            oneOf: [
                                {
                                    properties: {
                                        isTileShapeSvg: { enum: [true] },
                                        svgs: {
                                            type: 'array',
                                            title: 'Add/configure SVGS',
                                            items: {
                                                type: 'object',
                                                properties: {
                                                    svgPath: { type: 'string', title: 'SVG Path' },
                                                    svgWidth: {
                                                        type: 'string',
                                                        title: 'SVG Width',
                                                    },
                                                    svgHeight: {
                                                        type: 'string',
                                                        title: 'SVG Height',
                                                    },
                                                    svgColor: {
                                                        type: 'string',
                                                        title: 'SVG Color',
                                                    },
                                                },
                                            },
                                        },
                                    },
                                },
                            ],
                        },
                    },
                },
                {
                    properties: {
                        riskIndicatorType: { enum: ['Gauge chart'] },
                        showChartDashedOuterLine: {
                            type: 'boolean',
                            title: 'Show dashed outer line for chart',
                            default: true,
                        },
                        chartIndicatorColor: {
                            type: 'string',
                            title: 'Chart indicator color',
                        },
                        chartBackgroundColor: {
                            type: 'string',
                            title: 'Chart indicator background color',
                        },
                        chartDialColor: {
                            type: 'string',
                            title: 'Chart dial color',
                        },
                        chartOptions: {
                            type: 'array',
                            title: 'Chart Options',
                            items: {
                                type: 'object',
                                properties: {
                                    path: { type: 'string', description: 'Options Object Path' },
                                    value: {
                                        type: 'string',
                                        description: 'Chart Options as JSON string',
                                    },
                                },
                            },
                        },
                    },
                    dependencies: {
                        showChartDashedOuterLine: {
                            oneOf: [
                                {
                                    properties: {
                                        showChartDashedOuterLine: { enum: [true] },
                                        chartDashedLineColor: {
                                            type: 'string',
                                            title: 'Chart dashed line color',
                                        },
                                    },
                                },
                                {
                                    properties: {
                                        showChartDashedOuterLine: { enum: [false] },
                                    },
                                },
                            ],
                        },
                    },
                },
                {
                    properties: {
                        riskIndicatorType: { enum: ['Images'] },
                        images: {
                            type: 'array',
                            description: 'Add images in order from 0 to 4 to match with risk value',
                            items: {
                                type: 'object',
                                properties: {
                                    imageSrc: {
                                        type: 'string',
                                        title: 'Add image src',
                                    },
                                    imageAlt: {
                                        type: 'string',
                                        title: 'Add image alt',
                                    },
                                },
                            },
                        },
                    },
                },
            ],
        },
    },
};

const groups = [
    {
        'Style Editor': ['styles'],
        ...customSchemas.textSchema.TextSchema.group,
        ...customSchemas.styleSchema.StyleSchema.group,
        ...customSchemas.layoutSchema.LayoutSchema.group,
        Values: [
            'riskIndicatorTitle',
            'riskRatingsDataKey',
            'riskRatings',
            'displayRatingLabel',
            'addTabIndexToSelectedTile',
            'ariaLabelsRiskRatings',
            'tileAriaLabel',
            'riskIndicatorType',
            'isTileShapeSvg',
            'svgs',
            'showChartDashedOuterLine',
            'chartDashedLineColor',
            'chartIndicatorColor',
            'chartBackgroundColor',
            'chartDialColor',
            'chartOptions',
            'images',
        ],
    },
];

const RiskIndicatorWidget: commonTypes.IWidget = {
    schema,
    uiSchema: {
        'ui:groups': groups,
        ...customSchemas.textSchema.TextSchema.uiSchema,
        ...customSchemas.styleSchema.StyleSchema.uiSchema,
        ...customSchemas.layoutSchema.LayoutSchema.uiSchema,
        styles: {
            'ui:widget': 'CSSEditor',
        },
        riskIndicatorTitle: { 'ui:widget': 'ResourcePicker', 'ui:placeholder': '{{}}' },
        riskRatingsDataKey: { 'ui:widget': 'CustomTextInput' },
        riskRatings: { 'ui:widget': 'CustomTextInput' },
        displayRatingLabel: { 'ui:widget': 'CustomCheckbox' },
        isTileShapeSvg: { 'ui:widget': 'CustomCheckbox' },
        svgs: {
            items: {
                svgPath: {
                    'ui:widget': 'CustomTextInput',
                },
                svgWidth: {
                    'ui:widget': 'CustomTextInput',
                },
                svgHeight: {
                    'ui:widget': 'CustomTextInput',
                },
                svgColor: {
                    'ui:widget': 'CustomTextInput',
                },
            },
        },
        riskIndicatorType: { 'ui:widget': 'CustomSelect' },
        showChartDashedOuterLine: { 'ui:widget': 'CustomCheckbox' },
        chartDashedLineColor: { 'ui:widget': 'ThemeColorPicker' },
        chartOptions: {
            path: { 'ui:widget': 'CustomTextInput' },
            value: { 'ui:widget': 'CustomTextInput' },
        },
        chartIndicatorColor: { 'ui:widget': 'ThemeColorPicker' },
        chartBackgroundColor: { 'ui:widget': 'ThemeColorPicker' },
        chartDialColor: { 'ui:widget': 'ThemeColorPicker' },
        addTabIndexToSelectedTile: { 'ui:widget': 'CustomCheckbox' },
        ariaLabelsRiskRatings: { 'ui:widget': 'CustomTextInput' },
        tileAriaLabel: { 'ui:widget': 'CustomTextInput' },
        images: {
            items: {
                imageSrc: {
                    'ui:placeholder': 'https://',
                    'ui:widget': 'MediaPicker',
                    'ui:options': {
                        isImagePicker: true,
                    },
                },
                imageAlt: {
                    'ui:widget': 'ResourcePicker',
                    'ui:placeholder': '{{ }}',
                },
            },
        },
    },
    templateView: RiskIndicator,
    id: 'RiskIndicator',
    group: 'graphics',
    contentPlaceHolders: ['children'],
    initialData: () => ({}),
};

export default RiskIndicatorWidget;
